import { isDefined } from '@fi-shared/utils';

// child to parent additional hierarchy
const hierarchy = {
    Core: 'Module',
    User: 'Module.Core',
    BusinessUnit: 'Module.Core',
    Area: 'Module.Core',
    Cluster: 'Module.Core',
    ExtendSharing: 'Module.Core',
    Document: 'Module',
    MediaBank: 'Module',
    Forum: 'Module',
    NewForum: 'Module',
    Checklist: 'Module',
    ChecklistV2: 'Module',
    ChecklistV2Audit: 'Module',
    Statistic: 'Module',
    SMS: 'Module',
    Form: 'Module',
    Task: 'Module',
    TaskV2: 'Module',
    Onboarding: 'Module',
    Upgrade: 'Module',
    CustomerBilling: 'Module',
    Billing: 'GlobalSettings',
    Translation: 'Module',
};

export interface IChecklistV2RootState {
    _name: string;
    List: string;
    Add: {
        _name: string;
        AddCheckpointSections: {
            _name: string;
            EditCheckpoint: string;
        };
    };
    Edit: {
        _name: string;
        EditCheckpointSections: {
            _name: string;
            EditCheckpoint: string;
        };
    };
    View: string;
    ViewByChecklist: string;
    ViewByChecklistTemplate: string;
    Template: {
        _name: string;
        List: string;
        Add: {
            _name: string;
            AddCheckpointSections: {
                _name: string;
                EditCheckpoint: string;
            };
        };
        Edit: {
            _name: string;
            EditCheckpointSections: {
                _name: string;
                EditCheckpoint: string;
            };
        };
        Copy: {
            _name: string;
            AddCheckpointSections: {
                _name: string;
                EditCheckpoint: string;
            };
        };
    };
    Schedule: {
        _name: string;
        List: string;
        Add: string;
        Edit: string;
    };
    Report: {
        _name: string;
        Dashboard: string;
    };
}

class FiStateNamesHelper {
    // Auth states
    Login = {
        _name: '',
        Auth: '',
        LoginHelp: '',
        HelpOther: '',
        LicenceAgreement: '',
        ConfidentialAgreement: '',
        RequestAccess: '',
    };
    LoginV2 = {
        _name: '',
    };
    LoginToken = '';

    // root states
    Module = '';
    GlobalSettings = '';

    // home states
    Home = {
        _name: '',
        Post: {
            _name: '',
            Comment: '',
            View: '',
        },
    };
    PostView = '';

    // core states
    Core = {
        _name: '',
    };
    User = {
        _name: '',
        List: '',
        Add: '',
        AddV2: '',
        Edit: '',
        EditV2: '',
        AccessRequests: '',
    };
    BusinessUnit = {
        _name: '',
        List: '',
        Add: '',
        AddV2: '',
        Edit: '',
        EditV2: '',
    };
    Area = {
        _name: '',
        List: '',
        Add: '',
        View: '',
        Edit: '',
        Delete: '',
    };
    Cluster = {
        _name: '',
        List: '',
        Add: '',
        View: '',
        Edit: '',
    };
    ExtendSharing = {
        _name: '',
        Edit: '',
    };

    // system setup states
    Role = {
        _name: '',
        List: '',
        Add: '',
        View: '',
        Edit: '',
        RoleRelations: '',
        ShareContent: '',
    };
    ExtendedRoleRight = {
        _name: '',
        List: '',
        Add: '',
        View: '',
        Edit: '',
    };
    BusinessArea = {
        _name: '',
        List: '',
        Add: '',
        View: '',
        Edit: '',
    };
    OtherSettings = {
        _name: '',
        List: '',
        SupportContact: '',
        Smtp: '',
        RequestAccess: '',
        DataField: '',
        TransferOwnership: '',
        PK: {
            _name: '',
            Settings: '',
            UnitsMatching: '',
            RolesMatching: '',
            UsersMatching: '',
            UserImport: '',
            ImportLog: '',
        },
        Baloo: { _name: '', Settings: '', Matching: '', ManualSync: '', SyncLog: '' },
        Planday: {
            _name: '',
            Settings: '',
            RolesMatching: '',
            ImportUnits: ``,
            UnitsMatching: '',
            MatchUnitsWithAreas: ``,
            ImportUsers: '',
            UsersMatching: '',
            ImportLog: '',
        },
        SiteDesign: { _name: '', Settings: '', LoginEdit: '', BrandEdit: '' },
        LoginReminder: {
            _name: '',
            Edit: '',
            Overview: '',
            Log: '',
        },
    };
    Appearance = {
        _name: '',
        View: '',
        StartPage: '',
        CustomerSettings: '',
        Sidebar: {
            _name: '',
            _parentName: '',
            View: '',
            Edit: '',
        },
        ListView: {
            _name: '',
            _parentName: '',
            View: '',
            Edit: '',
        },
    };
    Tag = {
        _name: '',
        List: '',
        Add: '',
        View: '',
        Edit: '',
    };
    SiteMap = {
        _name: '',
        List: '',
        Add: '',
        View: '',
        Edit: '',
    };
    History = {
        _name: '',
        List: '',
    };
    Agreement = {
        _name: '',
        List: '',
        Edit: '',
        View: {
            _name: '',
            Version: '',
            Accepted: {
                _name: '',
                List: '',
                View: '',
            },
        },
        Create: '', //
    };
    Import = {
        _name: '',
        SystemObject: '',
        Handbook: {
            _name: '',
            MindMap: '',
            Word: '',
            WordExport: '',
        },
    };

    // module states
    Document = {
        _name: '',
        List: '',
        View: '',
        Add: '',
        Edit: '',
        Copy: '',
        Version: '',
        Restore: '',
        Template: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
            Add: '',
        },
    };

    MediaBank = {
        _name: '',
        List: '',
        View: '',
        Edit: '',
        Add: '',
    };
    Forum = {
        _name: '',
        List: '',
        View: '',
        Add: '',
        Edit: '',
        Post: {
            _name: '',
            View: '',
            Add: '',
        },
    };
    Checklist = {
        _name: '',
        List: '',
        View: '',
        Add: '',
        Edit: '',
        ViewCheckpointAnswer: '',
        Template: {
            _name: '',
            List: '',
            Add: '',
            View: '',
            Edit: '',
        },
        Report: {
            _name: '',
            List: '',
            View: '',
        },
    };

    ChecklistV2: IChecklistV2RootState = {
        _name: '',
        List: '',
        Add: {
            _name: '',
            AddCheckpointSections: {
                _name: '',
                EditCheckpoint: '',
            },
        },
        Edit: {
            _name: '',
            EditCheckpointSections: {
                _name: '',
                EditCheckpoint: '',
            },
        },
        View: '',
        ViewByChecklist: '',
        ViewByChecklistTemplate: '',
        Template: {
            _name: '',
            List: '',
            Add: {
                _name: '',
                AddCheckpointSections: {
                    _name: '',
                    EditCheckpoint: '',
                },
            },
            Edit: {
                _name: '',
                EditCheckpointSections: {
                    _name: '',
                    EditCheckpoint: '',
                },
            },
            Copy: {
                _name: '',
                AddCheckpointSections: {
                    _name: '',
                    EditCheckpoint: '',
                },
            },
        },
        Schedule: {
            _name: '',
            List: '',
            Add: '',
            Edit: '',
        },
        Report: {
            _name: '',
            Dashboard: '',
        },
    };

    ChecklistV2Audit: IChecklistV2RootState = {
        _name: '',
        List: '',
        Add: {
            _name: '',
            AddCheckpointSections: {
                _name: '',
                EditCheckpoint: '',
            },
        },
        Edit: {
            _name: '',
            EditCheckpointSections: {
                _name: '',
                EditCheckpoint: '',
            },
        },
        View: '',
        ViewByChecklist: '',
        ViewByChecklistTemplate: '',
        Template: {
            _name: '',
            List: '',
            Add: {
                _name: '',
                AddCheckpointSections: {
                    _name: '',
                    EditCheckpoint: '',
                },
            },
            Edit: {
                _name: '',
                EditCheckpointSections: {
                    _name: '',
                    EditCheckpoint: '',
                },
            },
            Copy: {
                _name: '',
                AddCheckpointSections: {
                    _name: '',
                    EditCheckpoint: '',
                },
            },
        },
        Schedule: {
            _name: '',
            List: '',
            Add: '',
            Edit: '',
        },
        Report: {
            _name: '',
            Dashboard: '',
        },
    };

    Statistic2 = {
        _name: '',
        Dashboard: '',
        Report: {
            _name: '',
            List: '',
            View: '',
        },
    };

    Form = {
        _name: '',
        List: '',
        View: '',
        Add: '',
        Edit: '',
        Copy: '',
        Report: {
            _name: '',
            List: '',
            View: '',
        },
    };

    Task = {
        _name: '',
        ToComplete: '',
        Completed: '',
        Status: '',
        Add: '',
        Edit: '',
        Details: '',
        Archived: '',
    };

    TaskV2 = {
        _name: '',
        Current: '',
        Completed: '',
        Cancelled: '',
        Archived: '',
        Report: {
            _name: '',
            Dashboard: '',
            Widget: '',
        },
        Add: '',
        Edit: '',
        Details: '',
    };

    Onboarding = {
        _name: '',
        List: '',
        Add: '',
        Edit: '',
        Copy: '',
        Active: '',
        View: '',
        Completion: '',
        Connection: {
            _name: '',
            Add: '',
            Edit: '',
        },
        Todo: {
            _name: '',
            List: '',
            View: '',
            Edit: {
                _name: '',
                Sections: {
                    _name: '',
                    Item: '',
                },
            },
            Add: {
                _name: '',
                Sections: {
                    _name: '',
                    Item: '',
                },
            },
            Copy: '',
        },
        Quiz: {
            _name: '',
            List: '',
            Edit: '',
            Add: '',
            Copy: '',
            View: '',
        },
        Report: {
            _name: '',
            Dashboard: '',
            Widget: {
                _name: '',
                Locations: {
                    _name: '',
                    Participants: {
                        _name: '',
                    },
                },
                ParticipantDetails: {
                    _name: '',
                    // todo here  will be step view state
                },
            },
        },
        AssignUsers: '',
        /*Synchronized: {
            AssignUsers: '',
        },*/
        Guide: '',
    };

    SMS = {
        _name: '',
        List: '',
        Costs: '',
        Copy: '',
        Add: '',
        View: '',
    };

    Upgrade = {
        _name: '',
        List: '',
        Edit: '',
        View: '',
        Add: '',
    };

    CustomerBilling = {
        _name: '',
        Customers: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
        },
        CustomerUnits: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
        },
        Invoice: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
            Add: '',
        },
        OtherCustomers: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
            Add: '',
        },
        Resellers: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
            Add: '',
        },
        Price: {
            _name: '',
            List: '',
            Edit: '',
            Add: '',
        },
    };

    Billing = {
        _name: '',
        Central: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
        },
        Unit: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
        },
        Payment: {
            _name: '',
            List: '',
            InvoiceInformation: {
                _name: '',
                View: '',
                Edit: '',
            },
            CreditCardInformation: {
                _name: '',
                View: '',
                Edit: '',
            },
        },
        CompanyInformation: {
            _name: '',
            List: '',
            View: '',
            Edit: '',
        },
    };

    Translation = {
        _name: '',
        List: '',
    };

    constructor() {
        // generate state names:
        // this.Role._name = 'Role'
        // this.Role.List = 'Role.List'
        // this.Role.Add = 'Role.Add' etc...
        this.updateStateNames(this, '');
    }

    private updateStateNames(obj, prefix?: string) {
        if (prefix && hierarchy[prefix]) {
            prefix = '' + hierarchy[prefix] + '.' + prefix;
        }

        if (isDefined(obj['_name'])) {
            obj['_name'] = prefix;
        }

        if (prefix) {
            prefix = prefix + '.';
        }

        for (let propName in obj) {
            if (!obj.hasOwnProperty(propName) || propName.startsWith('_')) continue;
            const propValue = obj[propName];

            if (typeof propValue === typeof '') {
                obj[propName] = prefix + propName;
            } else {
                this.updateStateNames(propValue, prefix + propName);
            }
        }
    }

    getChecklistV2RootState(auditMode: boolean) {
        return !auditMode ? this.ChecklistV2 : this.ChecklistV2Audit;
    }
}

export const FiStateNames = new FiStateNamesHelper();
